import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import type { FacilityHierarchyInput } from "@utils/facilityUtils";
import { getFacilityHierarchyId } from "@utils/facilityUtils";
import type { FacilityNetworkViewModel } from "@utils/facilityNetworkView";
import { getCurrentUserId } from "@utils/userUtils";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { getFacilityNetworkOverview } from "@services/facilityDashboard";
import {
  breakdownByFacilityType,
  collectLinkedMembers,
  countDistinctFacilityTypes,
  facilityExposureMeta,
} from "./facilityNetworkOverviewUtils";
import type { NetworkCompositionCounts } from "./facilityNetworkOverviewUtils";
import {
  mapNetworkOverviewApiToAggregate,
  type OverviewAggregateResult,
} from "./facilityNetworkOverviewAggregateUtils";
import FacilityNetworkCompositionBar from "./FacilityNetworkCompositionBar";
import FacilityNetworkFigmaCard from "./FacilityNetworkFigmaCard";
import FacilityNetworkFigmaHeading from "./FacilityNetworkFigmaHeading";
import FacilityNetworkPenaltyTrendChart from "./FacilityNetworkPenaltyTrendChart";
import { FN } from "./facilityNetworkTheme";
import {
  facilityDisplayName,
} from "./facilityNetworkChildrenUtils";
import { getNetworkLinkListFacilityId } from "./facilityNetworkHierarchyUtils";
import type { MyFacilityListRow } from "types/myFacilityListRow";

type Props = {
  networkView: FacilityNetworkViewModel;
  composition: NetworkCompositionCounts;
  onGoToRequests: () => void;
  onGoToLinkedFacilities: (typeFilter?: string) => void;
  onAddChildFacilities?: () => void;
  onOpenFacility?: (facility: FacilityHierarchyInput) => void;
  /** Preferred: open child dashboard inside Facility Network shell. */
  onViewChildDashboard?: (facility: MyFacilityListRow) => void;
};

function OverviewMetricCard({
  label,
  value,
  valueColor,
  sublabel,
  sublabelColor,
  loading,
}: {
  label: string;
  value: string;
  valueColor: string;
  sublabel?: string;
  sublabelColor?: string;
  loading?: boolean;
}) {
  return (
    <FacilityNetworkFigmaCard>
      <Typography
        sx={{
          fontFamily: FN.fontFamily,
          fontSize: 14,
          fontWeight: 600,
          color: FN.figmaAccent,
          lineHeight: "20px",
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <Box sx={{ py: 0.5 }}>
          <CircularProgress size={20} sx={{ color: FN.primary }} />
        </Box>
      ) : (
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 24,
            fontWeight: 600,
            color: valueColor,
            letterSpacing: FN.figmaTitleTracking,
            lineHeight: "32px",
            overflowWrap: "anywhere",
          }}
        >
          {value}
        </Typography>
      )}
      {sublabel ? (
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: 12,
            color: sublabelColor ?? FN.figmaAccent,
            letterSpacing: "0.03px",
            lineHeight: "16px",
            overflowWrap: "anywhere",
          }}
        >
          {sublabel}
        </Typography>
      ) : null}
    </FacilityNetworkFigmaCard>
  );
}

export default function FacilityNetworkParentOverviewTab({
  networkView,
  composition,
  onGoToRequests,
  onGoToLinkedFacilities,
  onAddChildFacilities,
  onOpenFacility,
  onViewChildDashboard,
}: Props) {
  const linked = collectLinkedMembers(networkView);
  const types = breakdownByFacilityType(linked);
  const linkedCount = composition.linkedCount;
  const typeCount = countDistinctFacilityTypes(linked);
  const parentId = getNetworkLinkListFacilityId(networkView);

  const [aggregate, setAggregate] = useState<OverviewAggregateResult | null>(null);
  const [loadingAggregate, setLoadingAggregate] = useState(false);

  const loadAggregate = useCallback(async () => {
    if (parentId == null || linkedCount === 0) {
      setAggregate(null);
      return;
    }
    const userId = Number(getCurrentUserId());
    if (!userId) return;

    setLoadingAggregate(true);
    try {
      const api = await getFacilityNetworkOverview(
        parentId,
        userId,
        getSelectedCountryCode(),
      );
      setAggregate(mapNetworkOverviewApiToAggregate(api));
    } catch {
      setAggregate(null);
    } finally {
      setLoadingAggregate(false);
    }
  }, [parentId, linkedCount]);

  useEffect(() => {
    void loadAggregate();
  }, [loadAggregate]);

  const gapSublabel = useMemo(() => {
    if (loadingAggregate) return "Loading gap data…";
    if (!aggregate) return "Unable to load gap data";
    if (aggregate.facilitiesWithGaps === 0) return "No active gaps across linked facilities";
    return `${aggregate.facilitiesWithGaps} of ${aggregate.loadedCount} linked facilities`;
  }, [aggregate, loadingAggregate]);

  if (linkedCount === 0) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, minWidth: 0, width: "100%" }}>
        <FacilityNetworkCompositionBar
          counts={composition}
          onManageRequests={onGoToRequests}
          onPendingClick={onGoToRequests}
          onInviteClick={onGoToRequests}
        />
        <FacilityNetworkFigmaHeading
          title="Overview linked facilities"
          subtitle="Aggregated data across your 0 consented, active facility links."
        />
        <FacilityNetworkFigmaCard>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1.5,
              py: 1,
            }}
          >
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.figmaNeutral,
                lineHeight: "20px",
              }}
            >
              You haven&apos;t linked any facilities yet
            </Typography>
            {onAddChildFacilities ? (
              <Button
                variant="contained"
                onClick={onAddChildFacilities}
                sx={{
                  textTransform: "none",
                  borderRadius: "8px",
                  bgcolor: FN.primary,
                  "&:hover": { bgcolor: FN.pressed },
                }}
              >
                Add child facilities
              </Button>
            ) : null}
          </Box>
        </FacilityNetworkFigmaCard>
      </Box>
    );
  }

  const topRows =
    aggregate?.topExposure?.length
      ? aggregate.topExposure
      : linked.slice(0, 5).map((facility) => {
          const id = getFacilityHierarchyId(facility);
          return {
            facilityId: id ?? -1,
            name: facilityDisplayName(facility),
            metaLine: facilityExposureMeta(facility),
            exposure: 0,
            exposureDisplay: loadingAggregate ? "…" : "—",
            facility,
            dashboardFacility: null as MyFacilityListRow | null,
          };
        });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <FacilityNetworkCompositionBar
        counts={composition}
        onManageRequests={onGoToRequests}
        onPendingClick={onGoToRequests}
        onInviteClick={onGoToRequests}
      />

      <FacilityNetworkFigmaHeading
        title="Overview linked facilities"
        subtitle={`Aggregated data across your ${linkedCount} consented, active facility link${linkedCount === 1 ? "" : "s"}.`}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 1,
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <OverviewMetricCard
          label="Total penalty exposure"
          value={aggregate?.totalPenaltyDisplay ?? "—"}
          valueColor={FN.figmaError}
          sublabel={
            loadingAggregate
              ? "Loading…"
              : aggregate
                ? `Across ${aggregate.loadedCount} linked facilit${aggregate.loadedCount === 1 ? "y" : "ies"}`
                : "Unable to load"
          }
          sublabelColor={FN.figmaError}
          loading={loadingAggregate}
        />
        <OverviewMetricCard
          label="Facilities with gaps"
          value={aggregate ? String(aggregate.facilitiesWithGaps) : "—"}
          valueColor={FN.figmaWarning}
          sublabel={gapSublabel}
          loading={loadingAggregate}
        />
        <OverviewMetricCard
          label="Facilities Clean"
          value={aggregate ? String(aggregate.facilitiesClean) : "—"}
          valueColor={FN.figmaSuccess}
          sublabel="Zero penalties in last 12 months"
          loading={loadingAggregate}
        />
        <OverviewMetricCard
          label="Surveys due · 90 days"
          value={aggregate ? String(aggregate.surveysDue90d) : "—"}
          valueColor={FN.figmaNeutral}
          sublabel={
            loadingAggregate
              ? "Loading…"
              : aggregate
                ? "Upcoming + overdue calendar items"
                : "Unable to load"
          }
          loading={loadingAggregate}
        />
      </Box>

      <FacilityNetworkFigmaCard padding={0} gap={0}>
        <Box sx={{ px: 2, py: 2, borderBottom: `1px solid ${FN.figmaCardBorder}` }}>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: FN.figmaAccent,
              lineHeight: "20px",
            }}
          >
            Breakdown by facility type
          </Typography>
          <Typography
            sx={{
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.figmaAccent,
              letterSpacing: "0.03px",
              lineHeight: "16px",
              mt: 0.5,
            }}
          >
            {linkedCount} facilit{linkedCount === 1 ? "y" : "ies"} across {typeCount} CMS-certified
            type{typeCount === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: { xs: "wrap", md: "nowrap" },
            px: 2,
            py: 1,
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}
        >
          {types.map((row, index) => {
            const gaps = aggregate?.gapsByType?.[row.type] ?? 0;
            const isLast = index === types.length - 1;
            return (
              <Box
                key={row.type}
                component="button"
                type="button"
                onClick={() => onGoToLinkedFacilities(row.type)}
                sx={{
                  display: "flex",
                  flex: "1 1 0%",
                  width: 0,
                  minWidth: { xs: "calc(50% - 4px)", md: 0 },
                  alignItems: "flex-start",
                  p: 1,
                  border: "none",
                  borderRight: isLast ? "none" : `1px solid ${FN.figmaCardBorder}`,
                  bgcolor: "#fff",
                  cursor: "pointer",
                  textAlign: "left",
                  boxSizing: "border-box",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, width: "100%", overflow: "hidden" }}>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      fontWeight: 600,
                      color: FN.figmaAccent,
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                      lineHeight: "16px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.type}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: FN.fontFamily,
                        fontSize: 24,
                        fontWeight: 600,
                        color: FN.primary,
                        letterSpacing: FN.figmaTitleTracking,
                        lineHeight: "32px",
                      }}
                    >
                      {row.count}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: FN.fontFamily,
                        fontSize: 12,
                        fontWeight: 400,
                        color: FN.muted,
                        letterSpacing: "0.03px",
                        lineHeight: "16px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {loadingAggregate
                        ? "— loading gaps"
                        : gaps === 1
                          ? "1 with active gaps"
                          : `${gaps} with active gaps`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </FacilityNetworkFigmaCard>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 1,
          alignItems: "stretch",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflow: "hidden",
        }}
      >
        <FacilityNetworkFigmaCard padding={0} gap={0}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
              p: 2,
              borderBottom: `1px solid ${FN.figmaCardBorder}`,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 14,
                  fontWeight: 600,
                  color: FN.figmaAccent,
                  lineHeight: "20px",
                }}
              >
                Penalty trend
              </Typography>
              <Typography
                sx={{
                  fontFamily: FN.fontFamily,
                  fontSize: 12,
                  color: FN.figmaAccent,
                  letterSpacing: "0.25px",
                  lineHeight: "16px",
                  mt: 0.5,
                }}
              >
                Historic penalties · all linked facilities
              </Typography>
            </Box>
            <Box
              sx={{
                bgcolor: FN.metricCardBg,
                border: `1px solid ${FN.panelBorder}`,
                borderRadius: "68px",
                px: 1.5,
                py: 0.5,
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 500, color: FN.primary, lineHeight: "16px" }}>
                Last 6 years
              </Typography>
            </Box>
          </Box>
          <FacilityNetworkPenaltyTrendChart
            years={aggregate?.trendYears ?? []}
            loading={loadingAggregate}
          />
        </FacilityNetworkFigmaCard>

        <FacilityNetworkFigmaCard padding={0} gap={0}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${FN.figmaCardBorder}` }}>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 14,
                fontWeight: 600,
                color: FN.figmaAccent,
                lineHeight: "20px",
              }}
            >
              Top exposure
            </Typography>
            <Typography
              sx={{
                fontFamily: FN.fontFamily,
                fontSize: 12,
                color: FN.figmaAccent,
                letterSpacing: "0.25px",
                lineHeight: "16px",
                mt: 0.5,
              }}
            >
              Facilities driving penalty exposure this quarter
            </Typography>
          </Box>
          {topRows.map((row) => {
            const canOpen =
              Boolean(onViewChildDashboard && "dashboardFacility" in row && row.dashboardFacility) ||
              Boolean(onOpenFacility && "facility" in row && row.facility);
            return (
              <Box
                key={row.facilityId}
                component={canOpen ? "button" : "div"}
                type={canOpen ? "button" : undefined}
                onClick={() => {
                  if (
                    onViewChildDashboard &&
                    "dashboardFacility" in row &&
                    row.dashboardFacility
                  ) {
                    onViewChildDashboard(row.dashboardFacility);
                    return;
                  }
                  if (onOpenFacility && "facility" in row && row.facility) {
                    onOpenFacility(row.facility as FacilityHierarchyInput);
                  }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 2,
                  py: 1,
                  border: "none",
                  borderBottom: `1px solid ${FN.figmaCardBorder}`,
                  bgcolor: "transparent",
                  cursor: canOpen ? "pointer" : "default",
                  width: "100%",
                  textAlign: "left",
                  font: "inherit",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      fontWeight: 600,
                      color: FN.figmaNeutral,
                      letterSpacing: "0.25px",
                      lineHeight: "16px",
                    }}
                  >
                    {row.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: FN.fontFamily,
                      fontSize: 12,
                      color: FN.figmaAccent,
                      letterSpacing: "0.25px",
                      lineHeight: "16px",
                    }}
                  >
                    {row.metaLine}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: FN.fontFamily,
                    fontSize: 12,
                    color: FN.figmaAccent,
                    letterSpacing: "0.25px",
                    flexShrink: 0,
                  }}
                >
                  {row.exposureDisplay}
                </Typography>
              </Box>
            );
          })}
        </FacilityNetworkFigmaCard>
      </Box>
    </Box>
  );
}
